import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";

import { axiosService } from "network/axios";
import { useAuthStyles } from "theme/hooks/useAuthStyles";
import { trackSignUp } from "utils/ga4";

import { getPasswordErrors, SignUpFormInputs } from "../constants";

const ACCOUNT_TYPE = "business";

type GoogleUserInfo = {
  email: string;
  family_name?: string;
  given_name?: string;
  picture?: string;
  verified_email?: boolean;
};

export const useSignUpLogic = () => {
  const navigate = useNavigate();

  const authStyles = useAuthStyles("customer");

  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");

  const [isFetchingProfile, setIsFetchingProfile] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSucceed, setIsSucceed] = useState(false);

  const [showError, setShowError] = useState(false);
  const [authErrorMessageText, setAuthErrorMessageText] = useState<string>("");

  const getErrorMessage = useCallback((err: any, fallback: string) => {
    return (
      err?.response?.data?.messages?.error ||
      err?.response?.data?.message ||
      err?.data?.messages?.error ||
      err?.data?.message ||
      fallback
    );
  }, []);

  const passwordErrors = useMemo(() => {
    return getPasswordErrors(password, passwordAgain);
  }, [password, passwordAgain]);

  const isPasswordValid = useMemo(() => {
    return passwordErrors.length === 0 && password.length > 0 && passwordAgain.length > 0;
  }, [password, passwordAgain, passwordErrors.length]);

  const goToSignIn = useCallback(() => {
    navigate("/auth/sign-in");
  }, [navigate]);

  const redirectToVerify = useCallback(
    (params: { userId: string; email?: string }) => {
      const query = new URLSearchParams();
      query.set("user_id", params.userId);
      if (params.email) {
        query.set("email", params.email);
      }

      navigate(`/auth/verify?${query.toString()}`);
    },
    [navigate]
  );

  const handleRegister = useCallback(
    async (values: SignUpFormInputs) => {
      if (!isPasswordValid) {
        return;
      }

      const payload = {
        ...values,
        password,
        type: ACCOUNT_TYPE,
      };

      try {
        setShowError(false);
        setAuthErrorMessageText("");
        setIsRegistering(true);
        const res = await axiosService.post("/user/create", payload);

        const userId = (res.data as any)?.user?.user_id?.toString() || Date.now().toString();
        trackSignUp(userId, "portal");

        setIsSucceed(true);
        // Save credentials for "Verify Later" flow
        sessionStorage.setItem("pending_auth", JSON.stringify({ email: values.email, password }));
        redirectToVerify({ userId, email: values.email });
      } catch (err: any) {
        const message = getErrorMessage(
          err,
          "Error in registering you. Please contact to support team."
        );

        setShowError(true);
        setAuthErrorMessageText(message);

      } finally {
        setIsRegistering(false);
      }
    },
    [getErrorMessage, isPasswordValid, password, redirectToVerify]
  );

  const handleGoogleSignUp = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsFetchingProfile(true);
      try {
        const { access_token } = tokenResponse;
        const url = `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${access_token}`;
        const { data } = await axios.get<GoogleUserInfo>(url);

        if (!data) {
          return;
        }

        const { email, family_name, given_name, picture, verified_email } = data;

        if (!verified_email) {
          // dispatch(
          //   setAlertMessageRequest({
          //     type: "error",
          //     message: "Your gmail account does not verified. please verify your gmail account.",
          //   })
          // );
          return;
        }

        const payload: any = {
          first_name: family_name,
          last_name: given_name,
          email,
          password: "",
          picture,
          by_google: true,
          type: ACCOUNT_TYPE,
        };

        try {
          setShowError(false);
          setAuthErrorMessageText("");
          setIsRegistering(true);
          const res = await axiosService.post("/user/create", payload);
          const userId = (res.data as any)?.user?.user_id?.toString() || Date.now().toString();
          trackSignUp(userId, "google");
          setIsSucceed(true);
          // Save credentials for "Verify Later" flow (Google sign up - mark as google)
          sessionStorage.setItem("pending_auth", JSON.stringify({ email, is_google_auth: true }));
          redirectToVerify({ userId, email });
        } catch (err: any) {
          const message = getErrorMessage(
            err,
            "This email is already registered. Please use a different email."
          );

          setShowError(true);
          setAuthErrorMessageText(message);

        } finally {
          setIsRegistering(false);
        }
      } catch (err: any) {
        console.log(err?.data?.message);
      } finally {
        setIsFetchingProfile(false);
      }
    },
  });

  return {
    authStyles,
    navigate,
    password,
    passwordAgain,
    setPassword,
    setPasswordAgain,
    passwordErrors,
    isPasswordValid,
    isFetchingProfile,
    isRegistering,
    isSucceed,
    setIsSucceed,
    showError,
    authErrorMessageText,
    setShowError,
    setAuthErrorMessageText,
    goToSignIn,
    handleGoogleSignUp,
    handleRegister,
  };
};
